import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Label,
  Badge,
  Spinner,
  Container,
  UncontrolledTooltip
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { toast, ToastContainer } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import { 
  listarTodosTickets, 
  obtenerTicket, 
  cambiarEstatusTicket,
  eliminarTicket,
  selectTickets,
  selectTotalTickets,
  selectLoading,
  selectError,
  selectFiltros,
  selectEstadisticas,
  selectEliminandoTicket,
  setFiltros
} from '../../slices/ticketsSlice';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { DatosSesion } from '../../Components/Common/DatosSesion';
import TicketDrawer from './TicketDrawer';
import PaginationComponent from './PaginationComponent';
import { useDebounce } from '../../hooks/useDebounce';
import './AdminTickets.css';

// Función para formatear fecha a hora de Venezuela (UTC-4)
const formatVenezuelaDate = (dateString) => {
  // Agregar 'Z' si no tiene timezone para que JS lo interprete como UTC
  const utcString = dateString && !dateString.endsWith('Z') && !dateString.includes('+') ? dateString + 'Z' : dateString;
  const date = new Date(utcString);
  return date.toLocaleString('es-VE', {
    timeZone: 'America/Caracas',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

const AdminTickets = () => {
  const dispatch = useDispatch();
  
  // Estados locales
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState(null);
  
  // Estados para paginación (como en solicitudes)
  const [pageSize, setPageSize] = useState(20);
  const [pageIndex, setPageIndex] = useState(0);
  
  // Hook de debounce para búsqueda (como en solicitudes)
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  
  // Referencia para el debounce timer (como en solicitudes)
  const searchTimerRef = useRef(null);
  
  // Selectores de Redux
  const tickets = useSelector(selectTickets);
  const totalTickets = useSelector(selectTotalTickets);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const filtros = useSelector(selectFiltros);
  const estadisticas = useSelector(selectEstadisticas);
  const eliminandoTicket = useSelector(selectEliminandoTicket);
  
  // Datos del usuario actual
  const userData = DatosSesion('debug');
  const isAdmin = userData?.perfil === 'Administrador' || userData?.perfil === 'MyHealth';
  const isFarmacia = userData?.perfil === 'Farmacia';
  const isDelivery = userData?.perfil === 'Delivery';
  const farmaciaId = userData?.farmacia || '';
  
  // Efecto para cargar los tickets iniciales (como en solicitudes)
  useEffect(() => {
    const cargarTicketsIniciales = () => {
      setIsLoading(true);
      
      // Construir objeto de filtros para el thunk incluyendo búsqueda y paginación
      const filtrosParams = {
        farmacia_id: (isFarmacia || isDelivery) ? farmaciaId : (filtros.farmacia_id || ''),
        estatus: filtros.estatus || '',
        prioridad: filtros.prioridad || '',
        categoria: filtros.categoria || '',
        search: debouncedSearchQuery?.trim() || '',
        limit: pageSize,
        offset: pageIndex * pageSize
      };
      
      // Llamada al thunk con objeto de parámetros
      dispatch(listarTodosTickets(filtrosParams))
        .finally(() => setIsLoading(false));
    };
    
    // Ejecutar la carga inicial (como en solicitudes)
    const timer = setTimeout(cargarTicketsIniciales, 100);
    
    // Limpiar el timer si el componente se desmonta
    return () => clearTimeout(timer);
  }, [dispatch, isAdmin, isFarmacia, isDelivery, farmaciaId, debouncedSearchQuery, filtros.estatus, filtros.prioridad, filtros.categoria, pageSize, pageIndex]); // eslint-disable-line

  // Efecto para resetear resultados cuando cambia la búsqueda (como en solicitudes)
  useEffect(() => {
    setNoResults(false);
  }, [debouncedSearchQuery]);
  
  // Manejar cambio de filtros
  const handleFilterChange = (name, value) => {
    dispatch(setFiltros({ [name]: value }));
  };
  
  // Limpiar filtros (como en solicitudes)
  const handleClearFilters = () => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    setSearchQuery("");
    dispatch(setFiltros({ estatus: '', prioridad: '', categoria: '', farmacia_id: '' }));
    setNoResults(false);
    setIsLoading(true);
    
    // Realizar la búsqueda inicial después de limpiar con objeto vacío
    const filtrosParams = {
      farmacia_id: '',
      estatus: '',
      prioridad: '',
      categoria: ''
    };
    dispatch(listarTodosTickets(filtrosParams))
      .finally(() => setIsLoading(false));
  };
  
  // Seleccionar ticket para ver detalles
  const handleSelectTicket = (ticket) => {
    setSelectedTicket(ticket);
    setDrawerOpen(true);
  };
  
  // Cerrar drawer
  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedTicket(null);
  };
  
  // Confirmar eliminación de ticket
  const handleDeleteClick = (e, ticket) => {
    e.stopPropagation();
    setTicketToDelete(ticket);
    setDeleteModal(true);
  };
  
  // Ejecutar eliminación
  const handleConfirmDelete = async () => {
    if (!ticketToDelete) return;
    try {
      await dispatch(eliminarTicket(ticketToDelete.id)).unwrap();
      setDeleteModal(false);
      setTicketToDelete(null);
    } catch (error) {
      console.error('Error al eliminar ticket:', error);
    }
  };
  
  // Cambiar estado de ticket
  const handleStatusChange = async (ticketId, nuevoEstatus) => {
    try {
      await dispatch(cambiarEstatusTicket({ ticketId, nuevoEstatus })).unwrap();
      // Recargar la lista
      dispatch(listarTodosTickets(filtros));
    } catch (error) {
      console.error('Error al cambiar estado:', error);
    }
  };
  
  // Obtener color de estado
  const getStatusColor = (estatus) => {
    switch (estatus) {
      case 'Abierto': return 'primary';
      case 'En Progreso': return 'warning';
      case 'Cerrado': return 'success';
      default: return 'secondary';
    }
  };
  
  // Obtener color de prioridad
  const getPriorityColor = (prioridad) => {
    switch (prioridad) {
      case 'Urgente': return 'danger';
      case 'Alta': return 'warning';
      case 'Media': return 'info';
      case 'Baja': return 'secondary';
      default: return 'secondary';
    }
  };
  
  // Obtener color de categoría para avatar
  const getCategoryColor = (categoria) => {
    switch (categoria) {
      case 'Soporte': return 'avatar-soporte';
      case 'Facturacion': return 'avatar-facturacion';
      default: return 'avatar-default';
    }
  };
  
  // Obtener icono de categoría
  const getCategoryIcon = (categoria) => {
    return categoria === 'Soporte' ? 'ri-tools-line' : 'ri-file-text-line';
  };
  
  
  return (
    <React.Fragment>
      <ToastContainer closeButton={false} limit={1} />
      <div className="page-content">
        <Container fluid>
          {/* Cards de Estadísticas - Estilo Dashboard Solicitudes */}
          <Row className="mb-3">
        {/* Total Tickets */}
        <Col xl={3} md={6}>
          <div 
            className="card card-animate overflow-hidden" 
            style={{ borderLeft: '4px solid var(--vz-primary)' }}
          >
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1 overflow-hidden">
                  {isLoading ? (
                    <div className="placeholder-glow">
                      <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                        <span className="placeholder col-8"></span>
                      </p>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                        <span className="placeholder col-4"></span>
                      </h4>
                    </div>
                  ) : (
                    <>
                      <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                        Total Tickets
                      </p>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                        {estadisticas.total || 0}
                      </h4>
                    </>
                  )}
                </div>
                <div className="flex-shrink-0">
                  {isLoading ? (
                    <div className="avatar-sm placeholder-glow">
                      <span className="placeholder rounded" style={{ width: '100%', height: '100%' }}></span>
                    </div>
                  ) : (
                    <div className="avatar-sm">
                      <span className="avatar-title bg-primary-subtle text-primary rounded fs-3">
                        <i className="ri-ticket-2-line"></i>
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-3">
                {isLoading ? (
                  <span className="placeholder-glow"><span className="placeholder col-5"></span></span>
                ) : (
                  <span className="badge bg-primary-subtle text-primary">
                    <i className="ri-database-2-line me-1"></i>Total
                  </span>
                )}
              </div>
            </div>
          </div>
        </Col>

        {/* Tickets Abiertos */}
        <Col xl={3} md={6}>
          <div 
            className="card card-animate overflow-hidden" 
            style={{ borderLeft: '4px solid var(--vz-warning)' }}
          >
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1 overflow-hidden">
                  {isLoading ? (
                    <div className="placeholder-glow">
                      <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                        <span className="placeholder col-6"></span>
                      </p>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                        <span className="placeholder col-4"></span>
                      </h4>
                    </div>
                  ) : (
                    <>
                      <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                        Tickets Abiertos
                      </p>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                        {estadisticas.abiertos || 0}
                      </h4>
                    </>
                  )}
                </div>
                <div className="flex-shrink-0">
                  {isLoading ? (
                    <div className="avatar-sm placeholder-glow">
                      <span className="placeholder rounded" style={{ width: '100%', height: '100%' }}></span>
                    </div>
                  ) : (
                    <div className="avatar-sm">
                      <span className="avatar-title bg-warning-subtle text-warning rounded fs-3">
                        <i className="ri-timer-line"></i>
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-3">
                {isLoading ? (
                  <span className="placeholder-glow"><span className="placeholder col-5"></span></span>
                ) : (
                  <span className="badge bg-warning-subtle text-warning">
                    <i className="ri-time-line me-1"></i>Pendientes
                  </span>
                )}
              </div>
            </div>
          </div>
        </Col>

        {/* En Progreso */}
        <Col xl={3} md={6}>
          <div 
            className="card card-animate overflow-hidden" 
            style={{ borderLeft: '4px solid var(--vz-info)' }}
          >
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1 overflow-hidden">
                  {isLoading ? (
                    <div className="placeholder-glow">
                      <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                        <span className="placeholder col-6"></span>
                      </p>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                        <span className="placeholder col-4"></span>
                      </h4>
                    </div>
                  ) : (
                    <>
                      <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                        En Progreso
                      </p>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                        {estadisticas.enProgreso || 0}
                      </h4>
                    </>
                  )}
                </div>
                <div className="flex-shrink-0">
                  {isLoading ? (
                    <div className="avatar-sm placeholder-glow">
                      <span className="placeholder rounded" style={{ width: '100%', height: '100%' }}></span>
                    </div>
                  ) : (
                    <div className="avatar-sm">
                      <span className="avatar-title bg-info-subtle text-info rounded fs-3">
                        <i className="ri-loader-line"></i>
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-3">
                {isLoading ? (
                  <span className="placeholder-glow"><span className="placeholder col-5"></span></span>
                ) : (
                  <span className="badge bg-info-subtle text-info">
                    <i className="ri-refresh-line me-1"></i>Activos
                  </span>
                )}
              </div>
            </div>
          </div>
        </Col>

        {/* Cerrados */}
        <Col xl={3} md={6}>
          <div 
            className="card card-animate overflow-hidden" 
            style={{ borderLeft: '4px solid var(--vz-success)' }}
          >
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1 overflow-hidden">
                  {isLoading ? (
                    <div className="placeholder-glow">
                      <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                        <span className="placeholder col-6"></span>
                      </p>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                        <span className="placeholder col-4"></span>
                      </h4>
                    </div>
                  ) : (
                    <>
                      <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                        Tickets Cerrados
                      </p>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                        {estadisticas.cerrados || 0}
                      </h4>
                    </>
                  )}
                </div>
                <div className="flex-shrink-0">
                  {isLoading ? (
                    <div className="avatar-sm placeholder-glow">
                      <span className="placeholder rounded" style={{ width: '100%', height: '100%' }}></span>
                    </div>
                  ) : (
                    <div className="avatar-sm">
                      <span className="avatar-title bg-success-subtle text-success rounded fs-3">
                        <i className="ri-shopping-bag-line"></i>
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-3">
                {isLoading ? (
                  <span className="placeholder-glow"><span className="placeholder col-5"></span></span>
                ) : (
                  <span className="badge bg-success-subtle text-success">
                    <i className="ri-check-double-line me-1"></i>Completados
                  </span>
                )}
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Filtros y Tabla - Estilo Solicitudes */}
      <div className="card">
        <div className="card-body">
          <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
            <div className="d-flex flex-wrap gap-2 align-items-center flex-grow-1">
              <div className="search-box">
                <input
                  id="buscar"
                  type="text"
                  className="form-control search"
                  placeholder="Buscar por título, identificador, usuario o contenido..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoComplete="off"
                  style={{ minWidth: "220px" }}
                />
                <i
                  className="ri-search-line search-icon"
                  style={{ pointerEvents: "none" }}
                ></i>
              </div>
              
              <select
                className="form-select"
                value={filtros.estatus}
                onChange={(e) => handleFilterChange('estatus', e.target.value)}
                style={{ width: "auto" }}
                title="Filtrar por estado"
              >
                <option value="">Estado</option>
                <option value="Abierto">Abierto</option>
                <option value="En Progreso">En Progreso</option>
                <option value="Cerrado">Cerrado</option>
                <option value="Duplicado">Duplicado</option>
              </select>
              
              <select
                className="form-select"
                value={filtros.prioridad}
                onChange={(e) => handleFilterChange('prioridad', e.target.value)}
                style={{ width: "auto" }}
                title="Filtrar por prioridad"
              >
                <option value="">Prioridad</option>
                <option value="Urgente">Urgente</option>
                <option value="Alta">Alta</option>
                <option value="Media">Media</option>
                <option value="Baja">Baja</option>
              </select>
              
              <select
                className="form-select"
                value={filtros.categoria}
                onChange={(e) => handleFilterChange('categoria', e.target.value)}
                style={{ width: "auto" }}
                title="Filtrar por categoría"
              >
                <option value="">Categoría</option>
                <option value="Soporte">Soporte</option>
                <option value="Facturacion">Facturación</option>
              </select>
            </div>
            <div className="d-flex gap-2">
              <button
                className={`btn ${
                  filtros.estatus || filtros.prioridad || filtros.categoria || searchQuery
                    ? "btn-warning"
                    : "btn-outline-secondary"
                }`}
                onClick={handleClearFilters}
              >
                <i className="ri-refresh-line me-1"></i>
                Limpiar Filtros
              </button>
            </div>
          </div>
                {isLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Cargando...</span>
                    </div>
                    <p className="text-muted mt-2">Cargando tickets...</p>
                  </div>
                ) : error ? (
                  <div className="text-center py-4">
                    <i className="ri-error-warning-line text-danger fs-3"></i>
                    <p className="text-danger mt-2">{error}</p>
                  </div>
                ) : tickets.length === 0 && !noResults ? (
                  <div className="text-center py-4">
                    <i className="ri-inbox-line text-muted fs-3"></i>
                    <p className="text-muted mt-2">No se encontraron tickets</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle table-nowrap mb-0">
                      <thead>
                        <tr>
                          <th style={{width: '90px'}}>ID</th>
                          <th>Título</th>
                          <th>Cliente</th>
                          <th>Estado</th>
                          <th>Prioridad</th>
                          <th>Categoría</th>
                          <th>Fecha</th>
                          {isAdmin && <th style={{width: '60px'}}>Acciones</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {tickets.map((ticket) => (
                          <tr 
                            key={ticket.id}
                            style={{cursor: 'pointer'}}
                            onClick={() => handleSelectTicket(ticket)}
                          >
                            <td>
                              <span className="badge bg-light text-dark fw-medium" style={{fontSize: '11px'}}>
                                {ticket.identificador || '—'}
                              </span>
                            </td>
                            <td>
                              <div>
                                <h6 className="mb-1">{ticket.titulo || 'Sin Título'}</h6>
                                {ticket.ultimo_mensaje && (
                                  <p className="text-muted mb-0" style={{fontSize: '12px'}}>
                                    {ticket.ultimo_mensaje.length > 60 
                                      ? `${ticket.ultimo_mensaje.substring(0, 60)}...`
                                      : ticket.ultimo_mensaje
                                    }
                                  </p>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className={`avatar-xs me-2 ${getCategoryColor(ticket.categoria)}`}>
                                  <span className="avatar-title rounded-circle bg-primary text-white">
                                    {ticket.usuario_nombre?.charAt(0)?.toUpperCase() || 'U'}
                                  </span>
                                </div>
                                <div>
                                  <h6 className="mb-0">{ticket.usuario_nombre || 'Usuario Desconocido'}</h6>
                                  <small className="text-muted">{ticket.usuario_email || 'Sin Email'}</small>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className={`badge badge-soft-${getStatusColor(ticket.estatus)}`}>
                                {ticket.estatus}
                              </span>
                            </td>
                            <td>
                              <span className={`badge badge-soft-${getPriorityColor(ticket.prioridad)}`}>
                                {ticket.prioridad}
                              </span>
                            </td>
                            <td>
                              <i className={`${getCategoryIcon(ticket.categoria)} text-muted me-1`}></i>
                              {ticket.categoria || 'Sin Categoría'}
                            </td>
                            <td>
                              <div>
                                <small className="text-muted">
                                  {ticket.creado ? formatVenezuelaDate(ticket.creado) : 'Sin Fecha'}
                                </small>
                                {ticket.mensajes?.length > 0 && (
                                  <div className="mt-1">
                                    <small className="text-muted">
                                      <i className="ri-message-2-line me-1"></i>
                                      {ticket.mensajes.length}
                                    </small>
                                  </div>
                                )}
                              </div>
                            </td>
                            {isAdmin && (
                              <td onClick={(e) => e.stopPropagation()}>
                                <Button
                                  color="danger"
                                  size="sm"
                                  outline
                                  className="btn-icon"
                                  onClick={(e) => handleDeleteClick(e, ticket)}
                                  title="Eliminar ticket"
                                  disabled={eliminandoTicket}
                                >
                                  <i className="ri-delete-bin-line"></i>
                                </Button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                
                {/* Paginador */}
                {!error && tickets.length > 0 && (
                  <PaginationComponent
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    totalItems={totalTickets}
                    setPageIndex={setPageIndex}
                    setPageSize={setPageSize}
                  />
                )}
              </div>
            </div>
        
          {/* Modal de confirmación de eliminación */}
          <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} centered>
            <ModalHeader toggle={() => setDeleteModal(false)} className="bg-danger-subtle">
              <i className="ri-error-warning-line me-2 text-danger"></i>
              Confirmar Eliminación
            </ModalHeader>
            <ModalBody>
              <p className="mb-2">¿Estás seguro de que deseas eliminar este ticket?</p>
              {ticketToDelete && (
                <div className="p-3 bg-light rounded">
                  <strong>{ticketToDelete.titulo}</strong>
                  <br />
                  <small className="text-muted">Creado por: {ticketToDelete.usuario_nombre}</small>
                </div>
              )}
              <p className="text-danger mt-3 mb-0">
                <i className="ri-alert-line me-1"></i>
                Esta acción eliminará el ticket, sus mensajes, adjuntos y notificaciones asociadas. No se puede deshacer.
              </p>
            </ModalBody>
            <ModalFooter>
              <Button color="light" onClick={() => setDeleteModal(false)} disabled={eliminandoTicket}>
                Cancelar
              </Button>
              <Button color="danger" onClick={handleConfirmDelete} disabled={eliminandoTicket}>
                {eliminandoTicket ? (
                  <><Spinner size="sm" className="me-1" />Eliminando...</>
                ) : (
                  <><i className="ri-delete-bin-line me-1"></i>Eliminar</>
                )}
              </Button>
            </ModalFooter>
          </Modal>
          
          {/* Drawer para detalle del ticket */}
          <TicketDrawer
            isOpen={drawerOpen}
            ticket={selectedTicket}
            tickets={tickets}
            onClose={handleCloseDrawer}
            onStatusChange={handleStatusChange}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AdminTickets;
